import { useEffect, useState } from 'react';
import { Account, ContactUserData, UserRoles, UserTeam } from '../../../types';
import { getAccountContactSubject, getSfUsersSubject, useAccounts } from '../../../store/Accounts';
import { getFormsSubject, updateForm } from '../../../store/BulkForm';
import { AccountSelectionForm, RoleSelectionForm, UserContactDetails } from '../../Compounds/UserCreation';
import { SKIP_ACCOUNT_ROLES, getRoleSubject, getRoles } from '../../../store/Role';
import { managesTeam, getUser, getDefaultPreferences, getAccounts } from '../../../store/User';
import { TeamSelectionForm } from '../../Compounds/UserCreation';
import { useAdviseTeam } from '../../Compounds/UserCreation/useAdviseTeam';
import { missingAdviseTeam } from '../../../utils/adviseRoles';
import { Team, getTeamsSubject, useTeamsStore } from '../../../store/Team';
import { UserFormProps } from '../../../store/PortalUser';
import {
  AccountCustomizationData,
  getAccountCustomizationsSubject,
  getSubAccounts,
  isCustom,
  useAccountCustomizationsStore
} from '../../../store/AccountCustomization';

export interface UserFormArgs {
  formId: string;
}

export const UserForm = ({ formId }: UserFormArgs) => {
  const teamManager = managesTeam(getUser());
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [formData, setFormData] = useState<Partial<UserFormProps | undefined>>(undefined);
  const [accountContacts, setAccountContacts] = useState<ContactUserData[]>([]);
  const [sfUsers, setSfUsers] = useState<ContactUserData[]>([]);
  const [roles, setRoles] = useState<UserRoles[]>(getRoles());
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>();
  const [teams, setTeams] = useState<Team[]>([]);
  const [accountTeams, setAccountTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<UserTeam | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([]);
  const [contacts, setContacts] = useState<ContactUserData[]>([]);
  const [allowAccount, setAllowAccount] = useState<boolean>(!teamManager);
  const [acctCust, setAcctCust] = useState<AccountCustomizationData[]>([]);
  const { fetchTeams } = useTeamsStore();
  const { getAccountContacts } = useAccounts();
  const { fetchAccountCustomizations } = useAccountCustomizationsStore();

  useEffect(() => {
    const acctTeams = selectedAccount?.id ? teams.filter((t) => t.account_id === selectedAccount?.id) : teams;
    setAccountTeams(acctTeams);
  }, [selectedAccount, accountTeams.length, teams.length]);

  // Component init
  useEffect(() => {
    const accountContactSub = getAccountContactSubject().subscribe((contactData) => {
      const contacts = contactData[formId] || [];
      setAccountContacts(contacts);
      setContacts(contacts);
    });
    const roleSub = getRoleSubject().subscribe((roles) => setRoles(roles));
    const sfUserSub = getSfUsersSubject().subscribe((sfUsers) => setSfUsers(sfUsers));
    const formDataSub = getFormsSubject().subscribe((data) => {
      const formIndex = data.findIndex((item) => item?.id === formId);
      setFormData(data[formIndex]);
    });
    const teamsSub = getTeamsSubject().subscribe((teams) => setTeams(teams));
    const accCustSub = getAccountCustomizationsSubject().subscribe((cust) => setAcctCust(cust));

    fetchTeams();
    fetchAccountCustomizations();
    setAccounts(getAccounts() || []);

    return () => {
      if (accountContactSub) accountContactSub?.unsubscribe();
      if (roleSub) roleSub?.unsubscribe();
      if (sfUserSub) sfUserSub?.unsubscribe();
      if (formDataSub) formDataSub?.unsubscribe();
      if (teamsSub) teamsSub.unsubscribe();
      if (accCustSub) accCustSub.unsubscribe();
    };
  }, []);

  const handleAccountChange = (acct: Account | undefined) => {
    setSelectedAccount(acct);

    const update: Partial<UserFormProps> = {
      ...formData,
      email: '',
      first_name: '',
      last_name: '',
      account_id: acct?.sfId,
      account_gri: acct?.id
    };

    const preferences = getDefaultPreferences();

    const accountCust = acctCust.find((i) => i.account_id === acct?.id);
    if (accountCust && accountCust.content) {
      if (accountCust.content.inventory_grid && isCustom(accountCust.content.inventory_grid)) {
        preferences.content.inventory_grid_All = accountCust.content.inventory_grid;
      }
      if (accountCust.content.widgets && isCustom(accountCust.content.widgets)) {
        preferences.content.widgets = accountCust.content.widgets;
      }
    }

    update.userCustomizations = preferences;

    updateForm(formId, update, 'user');
    if (acct) {
      getAccountContacts(formId, [acct.id, ...getSubAccounts(acct.id).map((sa) => sa.id)]);
    }
  };

  const teamMissing = (roleNames: string[], team: UserTeam | null) =>
    !teamManager && missingAdviseTeam(roleNames, team);

  const handleTeamChange = (team: UserTeam | null) => {
    setSelectedTeam(team);
    const update: Partial<UserFormProps> = {
      ...formData,
      team_id: team?.id,
      teamMissing: teamMissing(
        selectedRoles.map((role) => role.name as string),
        team
      )
    };
    updateForm(formId, update, 'user');
  };

  const adviseTeam = useAdviseTeam(
    selectedRoles.map((role) => role.name as string),
    teams,
    selectedTeam,
    () => handleTeamChange(null)
  );

  const handleRolesUpdate = (updatedRoles: string[]) => {
    setAllowAccount(!teamManager);
    setAccountContacts(contacts);

    const filteredRoles = roles.filter((role) => updatedRoles.includes(role.name as string));
    setSelectedRoles(filteredRoles);
    const hidesAccount = updatedRoles.some((item) => SKIP_ACCOUNT_ROLES.includes(item));
    const update: Partial<UserFormProps> = {
      ...formData,
      roles_attributes: filteredRoles,
      teamMissing: teamMissing(updatedRoles, selectedTeam),
      ...(hidesAccount && { account_id: undefined, account_gri: undefined })
    };
    updateForm(formId, update, 'user');
    if (hidesAccount) {
      setSelectedAccount(undefined);
      setAllowAccount(false);
      setAccountContacts(sfUsers);
    }
  };

  const selectedAccountId = () => {
    if (!selectedRoles.some((r) => r.name === 'Team Manager')) return null;

    return selectedAccount?.id;
  };

  const handleTeamCreate = async () => {
    setTeams(getTeamsSubject().value);
  };

  return (
    <>
      <form
        className="flex flex-wrap flex-row grow"
        data-testid="user-form"
        data-cy="bulk-user-form">
        <RoleSelectionForm
          selectedRoles={selectedRoles.map((role) => role.name)}
          updateRoles={handleRolesUpdate}
          drawer
          size="small"
          styles={`w-40 lg:w-64 my-1${!allowAccount ? ' mr-4' : ''}`}
        />
        {!teamManager && adviseTeam.active && (
          <TeamSelectionForm
            teams={adviseTeam.teams}
            selectedTeam={selectedTeam}
            setSelectedTeam={handleTeamChange}
            required={adviseTeam.active}
            helperText={adviseTeam.helperText}
            size="small"
            styles="w-40 lg:w-62 my-1 mr-4"
          />
        )}
        {allowAccount && (
          <AccountSelectionForm
            accounts={accounts}
            selectedAccount={selectedAccount || null}
            setSelectedAccount={handleAccountChange}
            size="small"
            styles="w-36 lg:w-64 my-1 mx-4"
          />
        )}
        {!teamManager && allowAccount && (
          <TeamSelectionForm
            handleTeamCreate={handleTeamCreate}
            selectedAccountId={selectedAccountId()}
            teams={accountTeams}
            selectedTeam={selectedTeam}
            setSelectedTeam={handleTeamChange}
            size="small"
            styles="w-40 lg:w-62 my-1 lg:mr-4"
          />
        )}
        <UserContactDetails
          accountContacts={accountContacts}
          formId={formId}
          userDetails={formData}
          disableAll={(allowAccount && !selectedAccount) || false}
        />
      </form>
    </>
  );
};
